import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

export const PlayerProvider = ({ children }) => {
  const { isSubscribed } = useAuth();
  const [nowPlaying, setNowPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Stream URL is provided ONLY by the (subscription-gated) settings endpoint.
  // Non-subscribers never receive it, so playback cannot start.
  const [streamUrl, setStreamUrl] = useState("");
  const [fallbacks, setFallbacks] = useState([]);

  // The stream URL is subscriber-gated on the server. Only a real 401/403 clears it — a network blip
  // or a slow token refresh must NEVER stop audio that is already playing (old root cause).
  const loadSettings = useCallback(async () => {
    try {
      const { data } = await endpoints.radioStream();
      setStreamUrl((u) => data?.url || u);
      setFallbacks(data?.fallbacks || []);
      return data?.url || "";
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 403) { setStreamUrl(""); setIsPlaying(false); }
      return "";
    }
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      for (let i = 0; i < 5; i++) {
        try {
          const { data } = await endpoints.nowPlaying();
          if (active) setNowPlaying(data);
          return;
        } catch {
          await new Promise((r) => setTimeout(r, 500 * (i + 1)));
        }
      }
    };
    load();
    loadSettings();
    const t = setInterval(load, 30000);
    // Re-fetch the gated stream URL when auth changes (e.g. right after paying).
    const onAuth = () => loadSettings();
    window.addEventListener("auth-changed", onAuth);
    return () => { active = false; clearInterval(t); window.removeEventListener("auth-changed", onAuth); };
  }, [loadSettings]);

  // Free preview ran out: stop now, resume automatically the moment payment is confirmed.
  const stoppedByPreview = useRef(false);
  const expirePreview = useCallback(() => { stoppedByPreview.current = true; setIsPlaying(false); setStreamUrl(""); }, []);
  useEffect(() => {
    if (!isSubscribed) return;
    loadSettings().then((u) => { if (u && stoppedByPreview.current) { stoppedByPreview.current = false; setIsPlaying(true); } });
  }, [isSubscribed, loadSettings]);

  const play = useCallback(() => setIsPlaying(true), []);
  const toggle = useCallback(() => setIsPlaying((p) => !p), []);
  const stop = useCallback(() => setIsPlaying(false), []);

  // Gated entry point for "Listen live": enforces Register/Login → Subscribe → Pay → Activate → Radio.
  // Audio can only start once the subscriber-gated stream URL is available.
  const requestPlay = useCallback(async () => {
    if (isPlaying) { setIsPlaying(false); return; }
    if (!isSubscribed) {
      const { data } = await endpoints.previewState("radio").catch(() => ({ data: { phase: "expired" } }));
      if (data.phase === "expired") { window.dispatchEvent(new CustomEvent("open-subscribe")); return; }
    }
    let url = streamUrl;
    if (!url) url = await loadSettings();
    if (!url) { window.dispatchEvent(new CustomEvent("open-subscribe")); return; }
    setIsPlaying(true);
  }, [isPlaying, isSubscribed, streamUrl, loadSettings]);

  const value = useMemo(
    () => ({ nowPlaying, isPlaying, play, toggle, stop, requestPlay, expanded, setExpanded, streamUrl, fallbacks, refreshStream: loadSettings, expirePreview }),
    [nowPlaying, isPlaying, play, toggle, stop, requestPlay, expanded, streamUrl, fallbacks, loadSettings, expirePreview]
  );

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};
